import { Game } from "../engine/core/Game";
import { Hero } from "../engine/rpg/Hero";
import { Party } from "../engine/rpg/Party";
import { World, type InteractionContext } from "../engine/rpg/World";
import type { NpcDef } from "../engine/world/TileMap";
import { tileset } from "./data/tiles";
import { MAPS } from "./data/maps";
import { MONSTERS } from "./data/monsters";
import { SKILLS } from "./data/skills";
import { ITEMS } from "./data/items";
import { CLASSES } from "./data/classes";
import { SONGS } from "./data/music";
import { loadCharacterAssets } from "../engine/render/CharacterModel";
import { TitleScene } from "./scenes/TitleScene";

const canvas = document.getElementById("viewport") as HTMLCanvasElement;
const uiRoot = document.getElementById("ui") as HTMLElement;

const heroClass = CLASSES[0];
const mageClass = CLASSES[1];
const clericClass = CLASSES[2];

const party = new Party([new Hero("Rowan", heroClass, 1), new Hero("Wren", mageClass, 1)], 30);
party.addItem("herb", 3);
party.addItem("leather-vest");
party.members[0].equipment.armor = ITEMS.find((i) => i.id === "leather-vest");
party.removeItem("leather-vest");

/**
 * Game-specific interaction logic. The engine handles plain dialogue on its
 * own; this hook only exists for NPCs that *do* something.
 */
async function onInteract(npc: NpcDef, ctx: InteractionContext): Promise<boolean> {
  const { world, say, ask } = ctx;

  switch (npc.tag) {
    case "inn": {
      const cost = 10;
      if (!(await ask(`Innkeeper: A bed is ${cost} gold. Shall I turn one down?`))) {
        await say("Innkeeper: Suit yourself. The moor is no softer.");
        return true;
      }
      if (world.party.gold < cost) {
        await say("Innkeeper: Come back when your purse is heavier.");
        return true;
      }
      world.party.gold -= cost;
      world.party.reviveAll();
      world.party.fullHeal();
      await say("You sleep until the light comes through the shutters.\nEveryone is fully restored.");
      return true;
    }

    case "recruit-cleric": {
      if (world.party.hasFlag("cleric-joined")) {
        await say("Sister Elowen: Lead on. I'll keep the wounds closed.");
        return true;
      }
      await say("Sister Elowen: You're the ones going to the cave, then.");
      if (!(await ask("Sister Elowen: Two of you is one short. May I come?"))) {
        await say("Sister Elowen: I'll be here when you change your mind.");
        return true;
      }
      world.party.members.push(new Hero("Elowen", clericClass, Math.max(1, world.party.leader.level)));
      world.party.setFlag("cleric-joined");
      await say("Sister Elowen joins the party!");
      // The follower train needs rebuilding now that there are three of us.
      ctx.reload();
      return true;
    }

    case "boss-dragon": {
      if (world.party.hasFlag("dragon-slain")) {
        await say("Only scorch marks and quiet remain.");
        return true;
      }
      await say("Something enormous uncurls in the dark.");
      await say("Whelp: You came a long way to be eaten.");
      const outcome = await ctx.fight(["dragon"]);
      if (outcome !== "win") {
        if (outcome === "fled") await say("You back out of the chamber. It lets you.");
        return true;
      }
      world.party.setFlag("dragon-slain");
      world.party.addItem("elixir", 2);
      world.party.gold += 300;
      await say("The whelp collapses, and the smoke stops.");
      await say("Among the bones: 300 gold and two Elixirs of Dawn.");
      await say("Aldreth will sleep easier tonight.\n\n— thank you for playing —");
      ctx.reload();
      return true;
    }

    default:
      // Fall through to the NPC's own dialogue.
      return false;
  }
}

const world = new World({
  tileset,
  maps: MAPS,
  monsters: MONSTERS,
  skills: SKILLS,
  items: ITEMS,
  classes: CLASSES,
  party,
  startMap: "town",
  respawn: { map: "inn", x: 5, y: 4 },
  songs: SONGS,
  onInteract,
});

// A close third-person camera shows far more detail than the old overhead
// view did, so the internal resolution is higher than it used to be.
const game = new Game({ canvas, uiRoot, virtualHeight: 900 });

// Characters are a loaded asset now, and every scene builds them
// synchronously, so nothing may start until it is in memory.
await loadCharacterAssets();
game.start(new TitleScene(world));

// Handy while developing; harmless in production.
(window as unknown as { game: Game; world: World }).game = game;
(window as unknown as { game: Game; world: World }).world = world;
